import { VatRateType, InvoiceType } from '../Types/invoicesTypes';

/**
 * Frontend helper for cross-border VAT detection and auto-suggestion.
 * Mirrors backend CrossBorderVatHelper logic.
 */

/**
 * EU country prefixes (ISO 3166-1 alpha-2) for VAT number validation.
 * Excludes PL (Poland) as it's domestic.
 */
const EU_PREFIXES = [
  'AT',
  'BE',
  'BG',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GR',
  'HR',
  'HU',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
] as const;

/**
 * Checks if NIP is a valid EU VAT number (has EU country prefix).
 * @param nip - Tax identification number
 * @returns True if NIP starts with EU country code
 */
export const isEuVatNumber = (nip?: string | null): boolean => {
  if (!nip) return false;

  const normalized = nip.trim().toUpperCase().replace(/[-\s]/g, '');
  return EU_PREFIXES.some((prefix) => normalized.startsWith(prefix));
};

/**
 * Determines if invoice should use VatEU based on buyer location and invoice type.
 * @param currency - Invoice currency
 * @param buyerNip - Buyer's tax ID
 * @param invoiceType - Type of invoice
 * @returns True if VatEU should be auto-suggested
 */
export const shouldUseVatEU = (
  currency?: string,
  buyerNip?: string | null,
  invoiceType?: InvoiceType
): boolean => {
  // Skip validation for Correction and Proforma (inherit from original)
  if (invoiceType === InvoiceType.Correction || invoiceType === InvoiceType.Proforma) {
    return false;
  }

  // EU buyer with valid VAT-UE number → reverse charge (VatEU)
  if (buyerNip && isEuVatNumber(buyerNip)) {
    return true;
  }

  return false;
};

/**
 * Returns suggested VatRateType for cross-border transactions.
 * @param currency - Invoice currency
 * @param buyerNip - Buyer's tax ID
 * @param invoiceType - Type of invoice
 * @returns Suggested VAT type (VatEU, VatZW, or null for standard)
 */
export const getSuggestedVatType = (
  currency?: string,
  buyerNip?: string | null,
  invoiceType?: InvoiceType
): VatRateType | null => {
  // EU buyer → VatEU (reverse charge)
  if (shouldUseVatEU(currency, buyerNip, invoiceType)) {
    return VatRateType.VatEU;
  }

  // EUR without EU VAT → might be export outside EU
  if (currency?.toUpperCase() === 'EUR' && !isEuVatNumber(buyerNip)) {
    return VatRateType.VatZW; // 0% export
  }

  // Domestic transaction → use standard rates
  return null;
};

/**
 * Generates human-readable message for VAT suggestion.
 * @param buyerNip - Buyer's tax ID
 * @param suggestedVatType - Suggested VAT type
 * @returns User-friendly message explaining the suggestion
 */
export const getVatSuggestionMessage = (
  buyerNip?: string | null,
  suggestedVatType?: VatRateType | null
): string | null => {
  if (suggestedVatType === VatRateType.VatEU && buyerNip) {
    return `Wykryto kontrahenta UE (NIP: ${buyerNip}). Zastosowano stawkę VatEU (0% WDT - odwrotne obciążenie).`;
  }

  if (suggestedVatType === VatRateType.VatZW) {
    return 'Faktura w EUR bez numeru VAT-UE. Rozważ użycie VatZW (0% eksport poza UE).';
  }

  return null;
};

/**
 * Validates if current VAT type matches cross-border requirements.
 * @param currency - Invoice currency
 * @param buyerNip - Buyer's tax ID
 * @param currentVatType - Currently selected VAT type
 * @param invoiceType - Type of invoice
 * @returns Validation warning message or null if valid
 */
export const validateCrossBorderVat = (
  currency?: string,
  buyerNip?: string | null,
  currentVatType?: VatRateType,
  invoiceType?: InvoiceType
): string | null => {
  const suggested = getSuggestedVatType(currency, buyerNip, invoiceType);

  if (suggested === VatRateType.VatEU && currentVatType !== VatRateType.VatEU) {
    return `UWAGA: Faktura dla kontrahenta UE (NIP: ${buyerNip}) powinna używać stawki VatEU (0% WDT - reverse charge). Obecna stawka: ${currentVatType}. Sprawdź czy nabywca ma ważny numer VAT-UE.`;
  }

  if (
    suggested === VatRateType.VatZW &&
    currentVatType !== VatRateType.VatZW &&
    currentVatType !== VatRateType.VatEU
  ) {
    return `UWAGA: Faktura w walucie ${currency} bez numeru VAT-UE może być eksportem poza UE. Rozważ użycie VatZW (0% eksport) zamiast ${currentVatType}.`;
  }

  return null;
};

/**
 * Extracts country code from EU VAT number.
 * @param nip - Tax identification number
 * @returns Country code (e.g., "DE", "FR") or null
 */
export const getCountryFromVatNumber = (nip?: string | null): string | null => {
  if (!nip || !isEuVatNumber(nip)) return null;

  const normalized = nip.trim().toUpperCase().replace(/[-\s]/g, '');
  const prefix = EU_PREFIXES.find((p) => normalized.startsWith(p));
  return prefix || null;
};
